import { P, match } from 'ts-pattern'

import { PermissionString } from 'lib/rbac'
import { SyncErrorTaskInput } from './types'

type RepushTarget = {
  service: 'QuickBooks_Online'
  entityName: string
  permission: PermissionString
}

export type SyncErrorRepush =
  | (RepushTarget & {
      entityType: 'PAYMENT'
      paymentId: string
    })
  | (RepushTarget & {
      entityType: 'CREDIT_NOTE'
      creditNoteId: string
    })
  | (RepushTarget & {
      entityType: 'CREDIT_NOTE_APPLICATION'
      creditNoteId: string
      ledgerTransactionId: string
    })

export const syncErrorRepush = (
  input: SyncErrorTaskInput
): SyncErrorRepush | undefined =>
  match(input)
    .with(
      {
        externalService: 'QuickBooks_Online',
        direction: 'PUSH',
        entityType: 'PAYMENT',
        errorCode: P.union(
          'QUICKBOOKS_AUTO_APPLY_CREDIT_ENABLED',
          'QUICKBOOKS_INVOICE_GROSS_TOTAL_MISMATCH',
          'QUICKBOOKS_INVOICE_BALANCE_MISMATCH'
        ),
        sequenceEntityId: P.string.minLength(1)
      },
      ({ sequenceEntityId }) => ({
        entityType: 'PAYMENT' as const,
        service: 'QuickBooks_Online' as const,
        entityName: 'payment',
        permission: 'org.invoice:sync' as const,
        paymentId: sequenceEntityId
      })
    )
    .with(
      {
        externalService: 'QuickBooks_Online',
        direction: 'PUSH',
        entityType: 'CREDIT_NOTE_APPLICATION',
        errorCode: P.union(
          'QUICKBOOKS_AUTO_APPLY_CREDIT_ENABLED',
          'QUICKBOOKS_MULTI_CURRENCY_DISABLED'
        ),
        sequenceEntityId: P.string.minLength(1),
        entitySnapshot: {
          snapshotType: 'CREDIT_NOTE',
          creditNoteId: P.string.minLength(1)
        }
      },
      ({ sequenceEntityId, entitySnapshot }) => ({
        entityType: 'CREDIT_NOTE_APPLICATION' as const,
        service: 'QuickBooks_Online' as const,
        entityName: 'credit note application',
        permission: 'org.creditNote:sync' as const,
        creditNoteId: entitySnapshot.creditNoteId,
        ledgerTransactionId: sequenceEntityId
      })
    )
    .with(
      {
        externalService: 'QuickBooks_Online',
        direction: 'PUSH',
        entityType: 'CREDIT_NOTE',
        errorCode: P.union(
          'QUICKBOOKS_AUTO_APPLY_CREDIT_ENABLED',
          'QUICKBOOKS_MULTI_CURRENCY_DISABLED'
        ),
        sequenceEntityId: P.string.minLength(1)
      },
      ({ sequenceEntityId }) => ({
        entityType: 'CREDIT_NOTE' as const,
        service: 'QuickBooks_Online' as const,
        entityName: 'credit note',
        permission: 'org.creditNote:sync' as const,
        creditNoteId: sequenceEntityId
      })
    )
    .otherwise(() => undefined)
